import { View, StyleSheet, Text } from 'react-native'
import { DS } from '../../../design-system'
import { createColumnHelper, flexRender, getCoreRowModel, useReactTable } from '@tanstack/react-table'
import { useState } from 'react'
import { observer } from 'mobx-react-lite'
import { SkeletonLoader } from '../../../screens/home-screen/SkeletonLoader'
import { monthDayAndTimeFormatterWithYear } from '../../../utils'
import { useQuery as useConvexQuery } from 'convex/react'
import { api } from '@ppwr/convex/_generated/api'

type GameModelType = {
  gameId: string
  gameType: string
  hash: string
  createdAt: number
  numberOfHands: number
  numberOfHandsPlayed: number
  numberOfHandsWon: number
}

const columnHelper = createColumnHelper<GameModelType>()

function formatGameType(gameType: string) {
  switch (gameType) {
    case 'daily10':
      return 'Daily10'
    case 'practice':
      return 'Practice'
    case 'private':
      return 'Private'
    case 'public':
      return 'Public'
    case 'community':
      return 'Community'
    default:
      return gameType || 'N/A'
  }
}

const columns = [
  columnHelper.accessor('gameId', {
    header: () => 'Game ID',
    cell: (info) => (
      <DS.Text selectable variant="Body Small/Regular">
        {info.getValue()}
      </DS.Text>
    ),
    footer: (info) => info.column.id
  }),
  columnHelper.accessor('gameType', {
    header: () => (
      <View style={styles.columnHeader}>
        <DS.Text selectable variant="Body Small/Regular">
          Type
        </DS.Text>
      </View>
    ),
    cell: (info) => (
      <View style={styles.centeredCell}>
        <DS.Text selectable variant="Body Small/Regular" textAlign="center">
          {formatGameType(info.getValue())}
        </DS.Text>
      </View>
    ),
    footer: (info) => info.column.id
  }),
  columnHelper.accessor('hash', {
    header: () => (
      <View style={styles.columnHeader}>
        <DS.Text selectable variant="Body Small/Regular">
          Share Code
        </DS.Text>
      </View>
    ),
    cell: (info) => (
      <View style={styles.centeredCell}>
        <DS.Text selectable variant="Body Small/Regular" textAlign="center">
          {info.getValue()}
        </DS.Text>
      </View>
    ),
    footer: (info) => info.column.id
  }),
  columnHelper.accessor('createdAt', {
    header: () => 'Created At',
    cell: (info) => (
      <DS.Text selectable variant="Body Small/Regular">
        {monthDayAndTimeFormatterWithYear(new Date(info.getValue()))}
      </DS.Text>
    ),
    footer: (info) => info.column.id
  }),
  columnHelper.accessor('numberOfHands', {
    header: () => (
      <View style={styles.columnHeader}>
        <DS.Text selectable variant="Body Small/Regular">
          Hands
        </DS.Text>
      </View>
    ),
    cell: (info) => (
      <View style={styles.centeredCell}>
        <DS.Text selectable variant="Body Small/Regular" textAlign="center">
          {info.getValue()}
        </DS.Text>
      </View>
    ),
    footer: (info) => info.column.id
  }),
  columnHelper.accessor('numberOfHandsPlayed', {
    header: () => (
      <View style={styles.columnHeader}>
        <DS.Text selectable variant="Body Small/Regular">
          Hands Played
        </DS.Text>
      </View>
    ),
    cell: (info) => (
      <View style={styles.centeredCell}>
        <DS.Text selectable variant="Body Small/Regular" textAlign="center">
          {info.getValue()}
        </DS.Text>
      </View>
    ),
    footer: (info) => info.column.id
  }),

  columnHelper.accessor('numberOfHandsWon', {
    header: () => (
      <View style={styles.columnHeader}>
        <DS.Text selectable variant="Body Small/Regular">
          Hands Won
        </DS.Text>
      </View>
    ),
    cell: (info) => (
      <View style={styles.centeredCell}>
        <DS.Text selectable variant="Body Small/Regular" textAlign="center">
          {info.getValue()}
        </DS.Text>
      </View>
    ),
    footer: (info) => info.column.id
  })
]

const PAGE_SIZE = 10

export const PlayerGamesTable = observer(({ id }: { id: string }) => {
  const [cursorStack, setCursorStack] = useState<(string | null)[]>([null])
  const currentCursor = cursorStack[cursorStack.length - 1]
  const currentPage = cursorStack.length

  const dashboardUser = useConvexQuery(api.players.queries.getDashboardUserData, {
    id,
    gamesPaginationOpts: { cursor: currentCursor, numItems: PAGE_SIZE }
  })

  const userGames = dashboardUser?.games ?? []
  const isDone = dashboardUser?.gamesPagination.isDone ?? true
  const continueCursor = dashboardUser?.gamesPagination.continueCursor ?? null
  const isLoading = dashboardUser === undefined

  const table = useReactTable({
    data: userGames,
    columns,
    getCoreRowModel: getCoreRowModel()
  })

  const handleNextPage = () => {
    if (continueCursor && !isDone) {
      setCursorStack((prev) => [...prev, continueCursor])
    }
  }

  const handlePrevPage = () => {
    setCursorStack((prev) => prev.slice(0, -1))
  }

  if (isLoading) {
    return <SkeletonLoader height={300} flexGrow={1} />
  }

  return (
    <View style={styles.container}>
      <DS.Text variant="Heading 2/Medium">Games History</DS.Text>
      <View style={styles.tableContainer}>
        <View style={styles.tableHeader}>
          {table.getHeaderGroups().map((headerGroup) => (
            <View style={styles.tableRow} key={headerGroup.id}>
              {headerGroup.headers.map((header) => (
                <View style={styles.tableHeaderCell} key={header.id}>
                  {header.isPlaceholder ? null : (
                    <Text style={styles.headerText}>
                      {flexRender(header.column.columnDef.header, header.getContext())}
                    </Text>
                  )}
                </View>
              ))}
            </View>
          ))}
        </View>
        <View style={styles.tableBody}>
          {table.getRowModel().rows.map((row) => (
            <View style={styles.tableRow} key={row.id}>
              {row.getVisibleCells().map((cell) => (
                <View style={styles.tableCell} key={cell.id}>
                  <Text style={styles.cellText}>{flexRender(cell.column.columnDef.cell, cell.getContext())}</Text>
                </View>
              ))}
            </View>
          ))}
        </View>
        <View style={styles.paginationContainer}>
          <View>
            <DS.Text variant="Body Small/Regular">Page {currentPage}</DS.Text>
          </View>
          <View style={styles.paginationButtonsContainer}>
            <DS.Button
              variant="Medium/Secondary"
              type="On Dark"
              disabled={isLoading || cursorStack.length <= 1}
              onPress={handlePrevPage}
            >
              Previous
            </DS.Button>
            <DS.Button
              variant="Medium/Secondary"
              type="On Dark"
              disabled={isLoading || isDone}
              onPress={handleNextPage}
            >
              Next Page
            </DS.Button>
          </View>
        </View>
      </View>
    </View>
  )
})

const styles = StyleSheet.create({
  container: {
    padding: 24,
    backgroundColor: DS.palette['Global/White'],
    borderColor: DS.palette['Brand/Light Grey'],
    borderWidth: 1,
    borderRadius: 12,
    flex: 1,
    flexGrow: 1
  },
  list: {
    paddingTop: 16,
    gap: 8
  },
  bullet: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between'
  },
  tableContainer: {
    width: '100%',
    borderRadius: 12,
    overflow: 'hidden',
    marginTop: 16,
    marginBottom: 16,
    borderColor: DS.palette['Brand/Light Grey'],
    borderWidth: 1
  },
  tableHeader: {
    backgroundColor: '#eeedea'
  },
  tableBody: {
    backgroundColor: 'white'
  },
  tableRow: {
    flexDirection: 'row'
  },
  tableHeaderCell: {
    flex: 1,
    borderBottomWidth: 2,
    borderBottomColor: '#dee2e6',
    padding: 16
  },
  tableCell: {
    flex: 1,
    padding: 16,
    borderBottomWidth: 1,
    borderBottomColor: '#e9ecef'
  },
  centeredCell: { width: '100%', flexDirection: 'row', justifyContent: 'center' },
  headerText: {
    fontWeight: '600',
    color: DS.palette['Brand/Dark Grey']
  },
  columnHeader: { width: '100%', flexDirection: 'row', justifyContent: 'center' },
  cellText: {
    color: '#212529'
  },
  paginationContainer: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: 8
  },
  paginationButtonsContainer: {
    flexDirection: 'row',
    gap: 8
  }
})
